import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductApi, PersonalCabinetApi, TransactionApi, AdminUserApi } from '../api';
import './AnalyticsPage.css';

type Company = { id: number; name: string };

interface ProductAnalytics {
  id: number;
  name: string;
  firm: string;
  model: string;
  size: string;
  price: number;
  acquisitionCost: number;
  deliveryCost: number;
  adsCost: number;
  otherCosts: number;
  totalCost: number;
  profitPerUnit: number;
  profitMargin: number;
  stock: number;
  totalSales: number;
  totalRevenue: number;
  totalProfit: number;
}

interface SalesOverTime {
  date: string;
  sales: number;
  revenue: number;
  profit: number;
}

interface UserStatsSummary {
  userId: number;
  username: string;
  role: string;
  addedProducts: number;
  soldProducts: number;
  earnedMoney: number;
  transactionsCount: number;
}

interface UserStatsFull {
  userId: number;
  username: string;
  role: string;
  addedProducts: number;
  soldProducts: number;
  earnedMoney: number;
  transactionsCount: number;
  byType: Record<string, number>;
  byProduct: Array<{
    productId: number;
    name: string;
    firm?: string;
    model?: string;
    size?: string;
    added: number;
    sold: number;
    earned: number;
  }>;
}

export default function AnalyticsPage() {
  const { t, i18n } = useTranslation();
  const role = localStorage.getItem('role');
  
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Analytics data
  const [productAnalytics, setProductAnalytics] = useState<ProductAnalytics[]>([]);
  const [salesOverTime, setSalesOverTime] = useState<SalesOverTime[]>([]);
  const [topProducts, setTopProducts] = useState<ProductAnalytics[]>([]);
  
  // Summary stats
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalProfit, setTotalProfit] = useState(0);
  const [avgProfitMargin, setAvgProfitMargin] = useState(0);
  const [totalSales, setTotalSales] = useState(0);

  // Admin-only user statistics
  const [userStats, setUserStats] = useState<UserStatsSummary[]>([]);
  const [selectedStatsUserId, setSelectedStatsUserId] = useState<number | null>(null);
  const [userStatsFull, setUserStatsFull] = useState<UserStatsFull | null>(null);
  const [adminStatsError, setAdminStatsError] = useState('');
  
  // Filters
  const [dateRange, setDateRange] = useState('30'); // days
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const asArray = <T,>(value: unknown): T[] => {
    if (Array.isArray(value)) return value as T[];
    if (value && typeof value === 'object') {
      const payload = value as { content?: unknown; data?: unknown };
      if (Array.isArray(payload.content)) return payload.content as T[];
      if (Array.isArray(payload.data)) return payload.data as T[];
    }
    return [];
  };

  const asNumber = (value: unknown) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatMoney = (value: unknown) =>
    `${new Intl.NumberFormat(i18n.resolvedLanguage || 'en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(asNumber(value))} ${t('analytics.currency')}`;

  useEffect(() => {
    loadCompanies();
    if (role === 'ROLE_ADMIN') {
      loadUserStats();
    }
    // Initial scope data is intentionally reloaded only when the authenticated role changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  useEffect(() => {
    if (role === 'ROLE_ADMIN' && selectedStatsUserId) {
      AdminUserApi.getUserFullStatistics(selectedStatsUserId)
        .then(res => { setUserStatsFull(res.data); setAdminStatsError(''); })
        .catch(() => { setUserStatsFull(null); setAdminStatsError(t('analytics.userStatsError')); });
    } else {
      setUserStatsFull(null);
    }
  }, [role, selectedStatsUserId, t]);

  const loadCompanies = async () => {
    try {
      const res = await PersonalCabinetApi.getPersonalCompanies();
      const list = asArray<Company>(res.data);
      setCompanies(list);
      
      const stored = localStorage.getItem('selectedCompanyId');
      const initial = stored && list.some((c: Company) => c.id === Number(stored))
        ? Number(stored)
        : list[0]?.id ?? null;
      setSelectedCompanyId(initial);
    } catch (error) {
      console.error('Failed to load companies:', error);
      setError(t('analytics.companiesError'));
    }
  };

  const loadAnalytics = useCallback(async () => {
    if (!selectedCompanyId) return;
    
    setLoading(true);
    setError('');
    try {
      // Load all products for the company
      const productsRes = await ProductApi.getByCompany(selectedCompanyId);
      const products = asArray<any>(productsRes.data);
      
      // Load transactions for the date range
      const filters: any = {};
      if (dateRange === 'custom') {
        if (startDate) filters.startDate = new Date(`${startDate}T00:00:00`).toISOString();
        if (endDate) filters.endDate = new Date(`${endDate}T23:59:59.999`).toISOString();
      } else {
        const days = Number(dateRange);
        if (Number.isFinite(days)) {
          const from = new Date();
          from.setDate(from.getDate() - days);
          filters.startDate = from.toISOString();
        }
      }
      
      const transactionsRes = await TransactionApi.getAllTransactions(selectedCompanyId, filters);
      const transactions = asArray<any>(transactionsRes.data);
      
      // Calculate analytics
      calculateAnalytics(products, transactions);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      setError(t('analytics.loadError'));
      calculateAnalytics([], []);
    } finally {
      setLoading(false);
    }
  // calculateAnalytics is a synchronous state projection of the values fetched above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, endDate, selectedCompanyId, startDate, t]);

  useEffect(() => {
    if (selectedCompanyId) loadAnalytics();
  }, [loadAnalytics, selectedCompanyId]);

  const loadUserStats = async () => {
    try {
      setAdminStatsError('');
      const res = await AdminUserApi.getAllUserStatistics();
      const stats = asArray<UserStatsSummary>(res.data);
      setUserStats(stats);
      if (stats.length > 0) {
        setSelectedStatsUserId(stats[0].userId);
      }
    } catch (error) {
      console.error('Failed to load user statistics:', error);
      setUserStats([]);
      setAdminStatsError(t('analytics.userStatsError'));
    }
  };

  const calculateAnalytics = (products: any[], transactions: any[]) => {
    // Calculate product-level analytics
    const productMap = new Map<number, ProductAnalytics>();
    
    products.forEach(product => {
      const acquisitionCost = asNumber(product.acquisitionCost);
      const deliveryCost = asNumber(product.deliveryCost);
      const adsCost = asNumber(product.adsCost);
      const otherCosts = asNumber(product.otherCosts);
      const totalCost = acquisitionCost + deliveryCost + adsCost + otherCosts;
      const price = asNumber(product.price);
      const profitPerUnit = price - totalCost;
      const profitMargin = price ? (profitPerUnit / price) * 100 : 0;
      
      productMap.set(product.id, {
        id: product.id,
        name: product.name,
        firm: product.firm,
        model: product.model,
        size: product.size,
        price,
        acquisitionCost,
        deliveryCost,
        adsCost,
        otherCosts,
        totalCost,
        profitPerUnit,
        profitMargin,
        stock: asNumber(product.stock),
        totalSales: 0,
        totalRevenue: 0,
        totalProfit: 0
      });
    });

    // Process transactions
    const salesTransactions = transactions.filter(t => t.transactionType === 'SALE');
    
    let totalRev = 0;
    let totalProf = 0;
    let totalSalesCount = 0;
    
    salesTransactions.forEach(transaction => {
      const productId = transaction.product?.id;
      if (!productId || !productMap.has(productId)) return;
      
      const analytics = productMap.get(productId)!;
      const quantity = asNumber(transaction.quantity);
      const revenue = asNumber(transaction.totalAmount);
      const profit = analytics.profitPerUnit * quantity;
      
      analytics.totalSales += quantity;
      analytics.totalRevenue += revenue;
      analytics.totalProfit += profit;
      
      totalRev += revenue;
      totalProf += profit;
      totalSalesCount += quantity;
    });

    const analyticsArray = Array.from(productMap.values());
    setProductAnalytics(analyticsArray);
    
    // Top products by profit
    const top = [...analyticsArray]
      .sort((a, b) => b.totalProfit - a.totalProfit)
      .slice(0, 10);
    setTopProducts(top);
    
    // Summary stats
    setTotalRevenue(totalRev);
    setTotalProfit(totalProf);
    setAvgProfitMargin(totalRev > 0 ? (totalProf / totalRev) * 100 : 0);
    setTotalSales(totalSalesCount);
    
    // Sales over time (group by day)
    const salesByDate = new Map<string, { sales: number; revenue: number; profit: number }>();
    salesTransactions.forEach(transaction => {
      const parsedDate = new Date(transaction.transactionDate);
      if (Number.isNaN(parsedDate.getTime())) return;
      const date = parsedDate.toISOString().split('T')[0];
      if (!salesByDate.has(date)) {
        salesByDate.set(date, { sales: 0, revenue: 0, profit: 0 });
      }
      const stats = salesByDate.get(date)!;
      const productId = transaction.product?.id;
      const analytics = productMap.get(productId);
      const quantity = asNumber(transaction.quantity);
      
      stats.sales += quantity;
      stats.revenue += asNumber(transaction.totalAmount);
      if (analytics) {
        stats.profit += analytics.profitPerUnit * quantity;
      }
    });
    
    const salesTimeline = Array.from(salesByDate.entries())
      .map(([date, stats]) => ({ date, ...stats }))
      .sort((a, b) => a.date.localeCompare(b.date));
    setSalesOverTime(salesTimeline);
  };

  return (
    <div className="analytics-page">
      {/* Header */}
      <div className="analytics-card analytics-header-card">
        <h1 style={{ margin: '0 0 20px', fontSize: 28, color: '#1976d2' }}>
          📊 {t('analytics.title')}
        </h1>
        
        {/* Filters */}
        <div className="analytics-filters">
          <div className="analytics-field">
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 14 }}>
              {t('common.company')}
            </label>
            <select
              value={selectedCompanyId || ''}
              onChange={e => {
                const id = e.target.value ? Number(e.target.value) : null;
                setSelectedCompanyId(id);
                if (id) localStorage.setItem('selectedCompanyId', id.toString());
              }}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', minWidth: 200 }}
              disabled={companies.length === 0}
            >
              {companies.length === 0 && <option value="">{t('analytics.noCompanies')}</option>}
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="analytics-field">
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 14 }}>
              {t('analytics.dateRange')}
            </label>
            <select
              value={dateRange}
              onChange={e => setDateRange(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }}
            >
              <option value="7">{t('analytics.last7Days')}</option>
              <option value="30">{t('analytics.last30Days')}</option>
              <option value="90">{t('analytics.last90Days')}</option>
              <option value="365">{t('analytics.lastYear')}</option>
              <option value="custom">{t('analytics.custom')}</option>
            </select>
          </div>

          {dateRange === 'custom' && (
            <>
              <div className="analytics-field">
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 14 }}>{t('analytics.from')}</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }}
                />
              </div>
              <div className="analytics-field">
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 14 }}>{t('analytics.to')}</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }}
                />
              </div>
            </>
          )}

          <button
            onClick={loadAnalytics}
            disabled={loading || !selectedCompanyId}
            className="analytics-refresh"
            style={{
              padding: '8px 20px',
              background: '#1976d2',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {loading ? t('common.loading') : t('common.refresh')}
          </button>
        </div>
      </div>

      {error && <div className="analytics-error" role="alert">{error}</div>}

      {loading ? (
        <div className="analytics-state" aria-live="polite">{t('analytics.loading')}</div>
      ) : companies.length === 0 ? (
        <div className="analytics-state">{t('analytics.noCompaniesHelp')}</div>
      ) : (
        <>
          {role === 'ROLE_ADMIN' && (
            <>
              {adminStatsError && <div className="analytics-error" role="status">{adminStatsError}</div>}
              <div className="analytics-card" style={{ background: '#fff', padding: 24, borderRadius: 8, marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h2 style={{ marginTop: 0, color: '#1976d2' }}>{t('analytics.allUsersStatistics')}</h2>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f3f7ff' }}>
                        <th style={{ textAlign: 'left', padding: 10 }}>{t('common.user')}</th>
                        <th style={{ textAlign: 'left', padding: 10 }}>{t('common.role')}</th>
                        <th style={{ textAlign: 'right', padding: 10 }}>{t('analytics.added')}</th>
                        <th style={{ textAlign: 'right', padding: 10 }}>{t('analytics.sold')}</th>
                        <th style={{ textAlign: 'right', padding: 10 }}>{t('analytics.earned')}</th>
                        <th style={{ textAlign: 'right', padding: 10 }}>{t('analytics.transactions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userStats.map(s => (
                        <tr key={s.userId} style={{ borderTop: '1px solid #e6e6e6' }}>
                          <td style={{ padding: 10 }}>{s.username}</td>
                          <td style={{ padding: 10 }}>{s.role}</td>
                          <td style={{ padding: 10, textAlign: 'right' }}>{s.addedProducts}</td>
                          <td style={{ padding: 10, textAlign: 'right' }}>{s.soldProducts}</td>
                          <td style={{ padding: 10, textAlign: 'right' }}>{formatMoney(s.earnedMoney)}</td>
                          <td style={{ padding: 10, textAlign: 'right' }}>{s.transactionsCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="analytics-card" style={{ background: '#fff', padding: 24, borderRadius: 8, marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h2 style={{ marginTop: 0, color: '#1976d2' }}>{t('analytics.userDetails')}</h2>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 14 }}>{t('analytics.selectUser')}</label>
                  <select
                    value={selectedStatsUserId || ''}
                    onChange={e => setSelectedStatsUserId(e.target.value ? Number(e.target.value) : null)}
                    style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', minWidth: 240 }}
                  >
                    {userStats.map(s => (
                      <option key={s.userId} value={s.userId}>
                        {s.username} ({s.role})
                      </option>
                    ))}
                  </select>
                </div>

                {userStatsFull && (
                  <>
                    <div style={{ marginBottom: 14, fontSize: 14 }}>
                      <strong>{userStatsFull.username}</strong> · {t('analytics.added')}: {userStatsFull.addedProducts} · {t('analytics.sold')}: {userStatsFull.soldProducts} · {t('analytics.earned')}: {formatMoney(userStatsFull.earnedMoney)} · {t('analytics.transactions')}: {userStatsFull.transactionsCount}
                    </div>

                    <div style={{ marginBottom: 16 }}>
                      <h4 style={{ marginBottom: 8 }}>{t('analytics.byTransactionType')}</h4>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        {Object.entries(userStatsFull.byType || {}).map(([type, qty]) => (
                          <span key={type} style={{ background: '#eef4ff', border: '1px solid #d9e7ff', borderRadius: 999, padding: '6px 10px', fontSize: 13 }}>
                            {t(`analytics.transactionTypes.${type}`, { defaultValue: type })}: {qty}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                      <h4 style={{ marginBottom: 8 }}>{t('analytics.byProduct')}</h4>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f8fbff' }}>
                            <th style={{ textAlign: 'left', padding: 10 }}>{t('common.product')}</th>
                            <th style={{ textAlign: 'right', padding: 10 }}>{t('analytics.added')}</th>
                            <th style={{ textAlign: 'right', padding: 10 }}>{t('analytics.sold')}</th>
                            <th style={{ textAlign: 'right', padding: 10 }}>{t('analytics.earned')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(userStatsFull.byProduct || []).map(p => (
                            <tr key={p.productId} style={{ borderTop: '1px solid #e8e8e8' }}>
                              <td style={{ padding: 10 }}>{p.name} {p.firm ? `- ${p.firm}` : ''} {p.model ? `- ${p.model}` : ''} {p.size ? `(${p.size})` : ''}</td>
                              <td style={{ padding: 10, textAlign: 'right' }}>{p.added}</td>
                              <td style={{ padding: 10, textAlign: 'right' }}>{p.sold}</td>
                              <td style={{ padding: 10, textAlign: 'right' }}>{formatMoney(p.earned)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {/* Summary Cards */}
          <div className="analytics-summary" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 20 }}>
            <div style={{ background: '#fff', padding: 20, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>{t('analytics.totalRevenue')}</div>
              <div style={{ fontSize: 32, fontWeight: 700, color: '#1976d2' }}>
                {formatMoney(totalRevenue)}
              </div>
            </div>
            
            <div style={{ background: '#fff', padding: 20, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>{t('analytics.totalProfit')}</div>
              <div style={{ fontSize: 32, fontWeight: 700, color: '#4caf50' }}>
                {formatMoney(totalProfit)}
              </div>
            </div>
            
            <div style={{ background: '#fff', padding: 20, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>{t('analytics.averageMargin')}</div>
              <div style={{ fontSize: 32, fontWeight: 700, color: '#ff9800' }}>
                {avgProfitMargin.toFixed(1)}%
              </div>
            </div>
            
            <div style={{ background: '#fff', padding: 20, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>{t('analytics.totalSales')}</div>
              <div style={{ fontSize: 32, fontWeight: 700, color: '#9c27b0' }}>
                {t('analytics.units', { count: totalSales })}
              </div>
            </div>
          </div>

          {/* Top Products by Profit */}
          <div className="analytics-card" style={{ background: '#fff', padding: 24, borderRadius: 8, marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ margin: '0 0 20px', fontSize: 20, color: '#333' }}>🏆 {t('analytics.topProducts')}</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                    <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t('common.product')}</th>
                    <th style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>{t('common.price')}</th>
                    <th style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>{t('analytics.totalCost')}</th>
                    <th style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>{t('analytics.profitPerUnit')}</th>
                    <th style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>{t('analytics.margin')}</th>
                    <th style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>{t('analytics.unitsSold')}</th>
                    <th style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>{t('analytics.totalProfit')}</th>
                  </tr>
                </thead>
                <tbody>
                  {topProducts.map((product, idx) => (
                    <tr key={product.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: 12 }}>
                        <div style={{ fontWeight: 600 }}>{product.name}</div>
                        <div style={{ fontSize: 12, color: '#666' }}>{product.firm} - {product.model} - {product.size}</div>
                      </td>
                      <td style={{ padding: 12, textAlign: 'right' }}>{formatMoney(product.price)}</td>
                      <td style={{ padding: 12, textAlign: 'right', color: '#c62828' }}>{formatMoney(product.totalCost)}</td>
                      <td style={{ padding: 12, textAlign: 'right', color: product.profitPerUnit >= 0 ? '#4caf50' : '#f44336' }}>
                        {formatMoney(product.profitPerUnit)}
                      </td>
                      <td style={{ padding: 12, textAlign: 'right' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: 4,
                          background: product.profitMargin >= 30 ? '#e8f5e9' : product.profitMargin >= 15 ? '#fff3e0' : '#ffebee',
                          color: product.profitMargin >= 30 ? '#4caf50' : product.profitMargin >= 15 ? '#ff9800' : '#f44336',
                          fontWeight: 600
                        }}>
                          {product.profitMargin.toFixed(1)}%
                        </span>
                      </td>
                      <td style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>{product.totalSales}</td>
                      <td style={{ padding: 12, textAlign: 'right', fontWeight: 700, color: '#4caf50' }}>
                        {formatMoney(product.totalProfit)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {topProducts.length === 0 && <div className="analytics-empty">{t('analytics.noProductData')}</div>}
            </div>
          </div>

          {/* Sales Over Time Chart (Simple Text-Based) */}
          {salesOverTime.length > 0 && (
            <div className="analytics-card" style={{ background: '#fff', padding: 24, borderRadius: 8, marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h2 style={{ margin: '0 0 20px', fontSize: 20, color: '#333' }}>📈 {t('analytics.salesTrend')}</h2>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                      <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t('common.date')}</th>
                      <th style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>{t('analytics.unitsSold')}</th>
                      <th style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>{t('analytics.revenue')}</th>
                      <th style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>{t('analytics.profit')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesOverTime.map(day => (
                      <tr key={day.date} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: 12 }}>{new Date(day.date).toLocaleDateString(i18n.resolvedLanguage)}</td>
                        <td style={{ padding: 12, textAlign: 'right' }}>{day.sales}</td>
                        <td style={{ padding: 12, textAlign: 'right', color: '#1976d2' }}>{formatMoney(day.revenue)}</td>
                        <td style={{ padding: 12, textAlign: 'right', color: '#4caf50', fontWeight: 600 }}>
                          {formatMoney(day.profit)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* All Products with Cost Breakdown */}
          <div className="analytics-card" style={{ background: '#fff', padding: 24, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ margin: '0 0 20px', fontSize: 20, color: '#333' }}>💰 {t('analytics.costBreakdown')}</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                    <th style={{ padding: 10, textAlign: 'left', fontWeight: 600 }}>{t('common.product')}</th>
                    <th style={{ padding: 10, textAlign: 'right', fontWeight: 600 }}>{t('common.price')}</th>
                    <th style={{ padding: 10, textAlign: 'right', fontWeight: 600 }}>{t('analytics.acquisition')}</th>
                    <th style={{ padding: 10, textAlign: 'right', fontWeight: 600 }}>{t('analytics.delivery')}</th>
                    <th style={{ padding: 10, textAlign: 'right', fontWeight: 600 }}>{t('analytics.ads')}</th>
                    <th style={{ padding: 10, textAlign: 'right', fontWeight: 600 }}>{t('analytics.other')}</th>
                    <th style={{ padding: 10, textAlign: 'right', fontWeight: 600 }}>{t('analytics.totalCost')}</th>
                    <th style={{ padding: 10, textAlign: 'right', fontWeight: 600 }}>{t('analytics.profitPerUnit')}</th>
                    <th style={{ padding: 10, textAlign: 'right', fontWeight: 600 }}>{t('analytics.margin')}</th>
                  </tr>
                </thead>
                <tbody>
                  {productAnalytics.map(product => (
                    <tr key={product.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: 10 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{product.name}</div>
                        <div style={{ fontSize: 11, color: '#999' }}>{product.firm} - {product.model} - {product.size}</div>
                      </td>
                      <td style={{ padding: 10, textAlign: 'right', fontWeight: 600 }}>{product.price.toFixed(2)}</td>
                      <td style={{ padding: 10, textAlign: 'right' }}>{product.acquisitionCost.toFixed(2)}</td>
                      <td style={{ padding: 10, textAlign: 'right' }}>{product.deliveryCost.toFixed(2)}</td>
                      <td style={{ padding: 10, textAlign: 'right' }}>{product.adsCost.toFixed(2)}</td>
                      <td style={{ padding: 10, textAlign: 'right' }}>{product.otherCosts.toFixed(2)}</td>
                      <td style={{ padding: 10, textAlign: 'right', fontWeight: 600, color: '#f44336' }}>
                        {product.totalCost.toFixed(2)}
                      </td>
                      <td style={{ padding: 10, textAlign: 'right', fontWeight: 600, color: product.profitPerUnit >= 0 ? '#4caf50' : '#f44336' }}>
                        {product.profitPerUnit.toFixed(2)}
                      </td>
                      <td style={{ padding: 10, textAlign: 'right' }}>
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: 3,
                          fontSize: 12,
                          fontWeight: 600,
                          background: product.profitMargin >= 30 ? '#e8f5e9' : product.profitMargin >= 15 ? '#fff3e0' : '#ffebee',
                          color: product.profitMargin >= 30 ? '#4caf50' : product.profitMargin >= 15 ? '#ff9800' : '#f44336'
                        }}>
                          {product.profitMargin.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {productAnalytics.length === 0 && <div className="analytics-empty">{t('analytics.noProductData')}</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
